import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  CreditCard, 
  Check, 
  Clock, 
  AlertTriangle, 
  Crown,
  Sparkles,
  Calendar,
  Users,
  Music2,
  Bell,
  Shield,
  Loader2,
  CheckCircle,
  XCircle
} from "lucide-react";

const Subscription = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState({});
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const isAdmin = user?.role === "admin";
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Check payment status if returning from checkout
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        apiClient.get("/subscription/plans"),
        apiClient.get("/subscription/status")
      ]);
      setPlans(plansRes.data.plans);
      setSubscriptionStatus(statusRes.data);
    } catch (error) {
      console.error("Failed to load subscription data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sid) => {
    setCheckingPayment(true);
    try {
      const res = await apiClient.get(`/subscription/checkout/${sid}/status`);
      if (res.data.payment_status === "paid") {
        toast.success("Payment successful! Your subscription is now active.");
        if (refreshUser) refreshUser();
        fetchData();
        // Clear the session_id from URL
        navigate("/subscription", { replace: true });
      } else if (res.data.status === "expired") {
        toast.error("Checkout session expired. Please try again.");
      }
    } catch (error) {
      console.error("Failed to check payment status:", error);
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!isAdmin) {
      toast.error("Only band admins can manage subscriptions");
      return;
    }

    setProcessingPlan(plan);
    try {
      const res = await apiClient.post("/subscription/checkout", {
        plan: plan,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checkout session");
      setProcessingPlan(null);
    }
  };

  const getStatusBadge = () => {
    if (!subscriptionStatus) return null;
    
    switch (subscriptionStatus.status) {
      case "trial":
        return (
          <Badge className="bg-blue-500/20 text-blue-400 text-sm px-3 py-1">
            <Clock className="w-4 h-4 mr-1" />
            Free Trial - {subscriptionStatus.days_remaining} days left
          </Badge>
        );
      case "active":
        return (
          <Badge className="bg-green-500/20 text-green-400 text-sm px-3 py-1">
            <CheckCircle className="w-4 h-4 mr-1" />
            Active Subscription
          </Badge>
        );
      case "expired":
        return (
          <Badge className="bg-red-500/20 text-red-400 text-sm px-3 py-1">
            <AlertTriangle className="w-4 h-4 mr-1" />
            Subscription Expired
          </Badge>
        );
      case "cancelled":
        return (
          <Badge className="bg-orange-500/20 text-orange-400 text-sm px-3 py-1">
            <XCircle className="w-4 h-4 mr-1" />
            Cancelled
          </Badge>
        );
      default:
        return null;
    }
  };

  const features = [
    { icon: Users, text: "Unlimited band members" },
    { icon: Calendar, text: "Event management & scheduling" },
    { icon: Music2, text: "Music library & repertoire" },
    { icon: Bell, text: "Email & push notifications" },
    { icon: Shield, text: "Attendance tracking & reports" },
    { icon: Sparkles, text: "Recurring events" }
  ];

  if (loading || checkingPayment) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">
          {checkingPayment ? "Verifying payment..." : "Loading..."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-heading font-bold flex items-center justify-center gap-3">
          <Crown className="w-8 h-8 text-primary" />
          Subscription
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage your band's subscription plan
        </p>
        <div className="mt-4">
          {getStatusBadge()}
        </div>
      </div>

      {/* Trial/Expired Warning */}
      {subscriptionStatus?.status === "trial" && subscriptionStatus.days_remaining <= 7 && (
        <Card className="border-orange-500/30 bg-orange-500/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-orange-500">Your trial ends soon!</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  You have {subscriptionStatus.days_remaining} days left in your free trial. 
                  Subscribe now to keep access to all features.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {subscriptionStatus?.status === "expired" && (
        <Card className="border-red-500/30 bg-red-500/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-500">Your subscription has expired</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Subscribe now to regain access to all features and continue managing your band.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan */}
      {subscriptionStatus?.status === "active" && (
        <Card className="glass-card border-green-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Current Plan: {subscriptionStatus.plan === "yearly" ? "Yearly" : "Monthly"}
            </CardTitle>
            <CardDescription>
              Your subscription is active until{" "}
              {new Date(subscriptionStatus.subscription_end).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric"
              })}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Pricing Cards */}
      {(subscriptionStatus?.status === "trial" || subscriptionStatus?.status === "expired") && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Monthly Plan */}
          <Card className="glass-card hover:border-primary/50 transition-colors">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl">Monthly</CardTitle>
              <div className="mt-4">
                <span className="text-4xl font-bold text-primary">£5</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-400" />
                    {feature.text}
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full" 
                onClick={() => handleSubscribe("monthly")}
                disabled={processingPlan === "monthly" || !isAdmin}
                data-testid="subscribe-monthly-btn"
              >
                {processingPlan === "monthly" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Subscribe Monthly
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Yearly Plan */}
          <Card className="glass-card border-primary/50 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-3 py-1 text-xs font-bold rounded-bl-lg">
              SAVE £10
            </div>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl">Yearly</CardTitle>
              <div className="mt-4">
                <span className="text-4xl font-bold text-primary">£50</span>
                <span className="text-muted-foreground">/year</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                That's just £4.17/month!
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-400" />
                    {feature.text}
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-primary hover:bg-primary/90" 
                onClick={() => handleSubscribe("yearly")}
                disabled={processingPlan === "yearly" || !isAdmin}
                data-testid="subscribe-yearly-btn"
              >
                {processingPlan === "yearly" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Subscribe Yearly & Save
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Features List */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-lg">What's Included</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                <feature.icon className="w-5 h-5 text-primary" />
                <span className="text-sm">{feature.text}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Admin Notice */}
      {!isAdmin && (
        <Card className="glass-card border-yellow-500/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium">Admin Access Required</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Only band admins can manage subscriptions. Contact your band admin to upgrade.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Subscription;
